import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '@/context/AuthContext';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { Card } from '@/components/ui/card';
import { Clock, MapPin, User as UserIcon, AlertTriangle, CheckCircle2, Wrench } from 'lucide-react';

const TABS = [
  { key: 'validation',  label: 'A valider',  statuses: ['envoyee'],
    empty: "Aucune mission en attente d'acceptation",
    description: "Missions envoyees, en attente que l'artisan accepte avec un creneau",
    icon: Clock, accent: 'amber' },
  { key: 'programmees', label: 'Programmees', statuses: ['acceptee', 'validee', 'en_cours'],
    empty: 'Aucune mission programmee',
    description: "Missions acceptees par l'artisan avec un creneau planifie",
    icon: Wrench, accent: 'blue' },
  { key: 'terminees',   label: 'Terminees', statuses: ['terminee'],
    empty: 'Aucune mission terminee',
    description: 'Missions cloturees avec photos par l\'artisan',
    icon: CheckCircle2, accent: 'emerald' },
];

const STATUS_BADGE = {
  envoyee:  { label: 'A valider', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  acceptee: { label: 'Programmee', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  validee:  { label: 'Programmee', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  en_cours: { label: 'Programmee', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  terminee: { label: 'Terminee', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  expiree:  { label: 'Expiree', cls: 'bg-red-100 text-red-700 border-red-300' },
  probleme: { label: 'Probleme', cls: 'bg-red-100 text-red-700 border-red-300' },
};

function getEventDate(mission) {
  const iso = mission.scheduled_at || mission.deadline_at || mission.created_at;
  return iso ? parseISO(iso) : null;
}

function fmtDate(d) {
  if (!d) return '';
  return format(d, "EEEE d MMMM 'à' HH'h'mm", { locale: fr });
}

export const MissionsPage = () => {
  const { token } = useAuth();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('validation');

  useEffect(() => {
    let cancelled = false;
    const fetchMissions = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/missions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled) setMissions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        // empty state will show
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) fetchMissions();
    return () => { cancelled = true; };
  }, [token]);

  const counts = useMemo(() => {
    const c = {};
    for (const t of TABS) {
      c[t.key] = missions.filter(m => t.statuses.includes(m.status)).length;
    }
    return c;
  }, [missions]);

  const visible = useMemo(() => {
    const tab = TABS.find(t => t.key === activeTab);
    if (!tab) return [];
    return missions
      .filter(m => tab.statuses.includes(m.status))
      .sort((a, b) => {
        // Programmées : tri par créneau (le plus proche d'abord)
        // Validation : par date de création (plus récent d'abord)
        // Terminées : par completed_at (plus récent d'abord)
        const aD = a.scheduled_at || a.completed_at || a.created_at;
        const bD = b.scheduled_at || b.completed_at || b.created_at;
        if (activeTab === 'programmees') return (aD || '').localeCompare(bD || '');
        return (bD || '').localeCompare(aD || '');
      });
  }, [missions, activeTab]);

  const currentTab = TABS.find(t => t.key === activeTab);

  return (
    <MainLayout>
      <Header title="Missions terrain" subtitle="Suivi des interventions assignées aux artisans" />

      <div className="p-6 space-y-4">

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-0">
          {TABS.map(t => {
            const active = activeTab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`relative px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 -mb-px border-b-2 ${
                  active
                    ? 'text-[#FF5A6E] border-[#FF5A6E]'
                    : 'text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-300'
                }`}
                data-testid={`mission-tab-${t.key}`}
              >
                <Icon size={15} />
                {t.label}
                <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                  active ? 'bg-[#FF5A6E] text-white' : 'bg-slate-100 text-slate-600'
                }`}>
                  {counts[t.key] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        {currentTab && (
          <p className="text-xs text-slate-400 px-1">{currentTab.description}</p>
        )}

        {loading ? (
          <div className="text-center text-slate-400 text-sm py-8">Chargement...</div>
        ) : visible.length === 0 ? (
          <Card className="p-12 text-center text-slate-400">
            <p className="text-sm">{currentTab?.empty}</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {visible.map(m => <MissionRow key={m.id} mission={m} />)}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

const MissionRow = ({ mission }) => {
  const status = STATUS_BADGE[mission.status] || { label: mission.status, cls: 'bg-slate-100 text-slate-700 border-slate-300' };
  const sevMajor = mission.issue_severity === 'probleme_majeur';
  const dt = getEventDate(mission);
  const sentTo = Array.isArray(mission.sent_to) ? mission.sent_to : [];
  const cats = Array.isArray(mission.issue_categories) ? mission.issue_categories.join(', ') : '';

  // The frontend has no /missions/:id route — link to the underlying issue,
  // which already shows the mission card with photos + notes.
  const targetHref = mission.issue_id ? `/issues/${mission.issue_id}` : '#';

  return (
    <Link to={targetHref} className="block">
      <Card className="p-4 hover:border-[#FF5A6E] hover:shadow-md transition cursor-pointer">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${status.cls}`}>
            {status.label}
          </span>
          {sevMajor && (
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold border bg-red-100 text-red-700 border-red-300 inline-flex items-center gap-1">
              <AlertTriangle size={11} /> Urgent
            </span>
          )}
          {cats && <span className="text-xs text-slate-500">· {cats}</span>}
        </div>

        <p className="font-semibold text-slate-900 text-sm mb-1">{mission.property_name || 'Propriété'}</p>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          {mission.property_address && (
            <span className="inline-flex items-center gap-1"><MapPin size={12} /> {mission.property_address}</span>
          )}
          {dt && (
            <span className="inline-flex items-center gap-1 text-slate-700 font-medium">
              <Clock size={12} /> {fmtDate(dt)}
            </span>
          )}
          {sentTo.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <UserIcon size={12} /> {sentTo.map(c => c.company_name).join(', ')}
            </span>
          )}
        </div>

        {mission.acceptance_note && (
          <p className="mt-2 text-xs text-slate-600 italic bg-blue-50 border-l-2 border-blue-300 px-2.5 py-1.5 rounded-r">
            <strong className="not-italic text-blue-700">Note artisan :</strong> {mission.acceptance_note}
          </p>
        )}
        {mission.completion_note && mission.status === 'terminee' && (
          <p className="mt-2 text-xs text-slate-600 italic bg-emerald-50 border-l-2 border-emerald-300 px-2.5 py-1.5 rounded-r">
            <strong className="not-italic text-emerald-700">Compte-rendu :</strong> {mission.completion_note}
          </p>
        )}
        {mission.status === 'terminee' && Array.isArray(mission.completion_images) && mission.completion_images.length > 0 && (
          <div className="mt-2 flex gap-1.5">
            {mission.completion_images.slice(0, 4).map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`photo ${i + 1}`}
                className="w-12 h-12 rounded border border-slate-200 object-cover"
              />
            ))}
            {mission.completion_images.length > 4 && (
              <div className="w-12 h-12 rounded border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-slate-500">
                +{mission.completion_images.length - 4}
              </div>
            )}
          </div>
        )}
      </Card>
    </Link>
  );
};

export default MissionsPage;
